// @flow

import React, { Component } from 'react';
import cx from 'classnames';
import ExperimentsClient from 'app/common/lib/ExperimentsClient';
import i18n from 'app/common/lib/i18n';
import legacyWithExperiments from 'app/common/lib/react/deprecated/legacyWithExperiments'; // eslint-disable-line pinterest/no-deprecated-imports
import PropTypes from 'prop-types';
import type { ContextLogFunc } from 'app/common/redux/actions/contextLog';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import UnauthFixedHeader from 'app/common/react/components/growth/unauth/UnauthUserProfilePage/UnauthFixedHeader';
import urlUtil from 'app/common/lib/urlUtil';
import withContextLog from 'app/common/lib/react/deprecated/withContextLog'; // eslint-disable-line pinterest/no-deprecated-imports
import withViewer from 'app/common/lib/react/withViewer';
import { compose } from 'redux';
import { prettifyUrl } from 'app/packages/url/formatUrl';
import { prettyNumberFormat } from 'app/common/lib/locale';
import {
  SignupSource,
  SignupTypes,
} from 'app/common/react/components/growth/unauth/signup/constants';
import { Box, Column, Container, Heading, Text } from 'gestalt';

type Props = {
  contextLog: ContextLogFunc,
  experiments: ExperimentsClient,
  scrollStep: 1 | 2 | 3,
  showFixedHeaderName: ?boolean,
  showSignupModal: (?string, ?string) => void,
  user: ProfileUser,
  viewer: Viewer,
};

type Context = {|
  locale: string,
  isBot: ?boolean,
  country: ?string,
  httpReferrer: ?string,
|};

const styles = {
  avatarStyle: {
    width: '168px',
    height: '168px',
    borderRadius: '84px',
  },
};
function formatDisplayUrl(url: string) {
  const prettyUrl = prettifyUrl(url);
  return urlUtil.punycode_decode(prettyUrl);
}

export class ProfileUnauthHeader extends Component<Props> {
  context: Context;
  container: ?HTMLElement;
  header: ?HTMLElement = null;

  showSignupModalWrapper = () => {
    const { showSignupModal } = this.props;

    showSignupModal(SignupTypes.signup, SignupSource.userProfilePage);
  };

  renderCreatorInformation = () => {
    const { scrollStep, user } = this.props;
    const { locale } = this.context;

    const followerCount = prettyNumberFormat(locale, user.follower_count);
    const followingCount = prettyNumberFormat(locale, user.following_count);

    const followerCountComponent = (
      <React.Fragment>
        <Text bold size="lg" inline overflow="normal">
          {followerCount}
        </Text>
        &nbsp;
        <Text size="lg" inline overflow="normal">
          {i18n._('Followers', 'Number of followers label')}
        </Text>
      </React.Fragment>
    );
    const followingCountComponent = (
      <React.Fragment>
        <Text bold size="lg" inline overflow="normal">
          {followingCount}
        </Text>
        &nbsp;
        <Text size="lg" inline overflow="normal">
          {i18n._('Following', 'Number of following label')}
        </Text>
      </React.Fragment>
    );

    return (
      <div
        ref={(ref) => {
          this.container = ref;
        }}
      >
        <React.Fragment>
          <Box display="flex" alignItems="start">
            <Column span={3}>
              <Box
                paddingX={2}
                marginLeft={-3}
                dangerouslySetInlineStyle={{ __style: { marginTop: 10 } }}
              >
                <div className={cx('AvatarScroller', { scroll: scrollStep }, `step${scrollStep}`)}>
                  <div className="ShadowedAvatarWrapper">
                    <img
                      alt={user.full_name || user.username}
                      src={user.image_xlarge_url}
                      style={styles.avatarStyle}
                    />
                  </div>
                </div>
              </Box>
            </Column>
            <Box alignSelf="start" column={9}>
              <Box
                alignItems="start"
                dangerouslySetInlineStyle={{ __style: { marginTop: 20 } }}
                display="flex"
                justifyContent="between"
                wrap
              >
                <Box column={7} paddingX={2} flex="grow" marginBottom={2}>
                  <Heading mdSize="sm" size="xs" accessibilityLevel={1}>
                    {user.full_name || user.username}
                  </Heading>
                  <Box display="flex" marginTop={4}>
                    <Box display="flex">
                      {followerCountComponent}
                      <Box display="flex">
                        <Box marginLeft={3} marginRight={3}>
                          <Text size="md">•</Text>
                        </Box>
                        {followingCountComponent}
                      </Box>
                      {user.domain_url && (
                        <Box display="flex">
                          <Box marginLeft={3} marginRight={3}>
                            <Text size="md">•</Text>
                          </Box>
                          <a rel="nofollow" href={user.domain_url ? `//${user.domain_url}` : ''}>
                            <Text bold inline truncate size="lg">
                              {formatDisplayUrl(user.domain_url)}
                            </Text>
                          </a>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box display="flex" marginTop={2}>
                    <Text bold size="lg">
                      {user.location}
                    </Text>
                  </Box>
                  <Box marginTop={2}>
                    <Text size="lg">{user.about}</Text>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </React.Fragment>
      </div>
    );
  };

  render() {
    return (
      <React.Fragment>
        <UnauthFixedHeader
          showFixedHeaderName={this.props.showFixedHeaderName}
          showSignupModalWrapper={this.showSignupModalWrapper}
          user={this.props.user}
        />
        <Container>{this.renderCreatorInformation()}</Container>
      </React.Fragment>
    );
  }
}

ProfileUnauthHeader.contextTypes = {
  locale: PropTypes.string.isRequired,
  country: PropTypes.string,
  isBot: PropTypes.bool,
  httpReferrer: PropTypes.string,
};

export default compose(
  withViewer,
  withContextLog,
  legacyWithExperiments,
)(ProfileUnauthHeader);
